/** Tier-authored civic silhouettes. Metres; +Z is the public entrance.
 * Independent streams keep massing decisions separate from decorative detail. */
import * as pmath from '../pmath.js';
import {rngFromString} from '../rng.js';
import type {AssetNode, AssetVec3} from '../proceduralAssets.js';
import type {FrontierPlacePlan, PlaceBox} from './frontierTown.js';
import {buildCivicHall,type CivicInterior} from './civicHall.js';

export const CIVIC_TIERS = [
 {seat:'Warden’s Post',ward:'Wayside Ward',width:4.4,depth:4,height:3.2,tower:0,platform:.32,wardRadius:1.45,crystal:1.6,guardians:1,spires:2},
 {seat:'Elder’s House',ward:'Hearth Ward',width:5.6,depth:4.8,height:3.9,tower:0,platform:.32,wardRadius:1.9,crystal:2.4,guardians:1,spires:2},
 {seat:'Village Hall',ward:'Gathering Ward',width:7.4,depth:6,height:5.8,tower:0,platform:.48,wardRadius:2.5,crystal:3.8,guardians:2,spires:2},
 {seat:'Town Hall',ward:'Covenant Ward',width:9.5,depth:7.2,height:6.6,tower:10,platform:.96,wardRadius:3.2,crystal:5.4,guardians:2,spires:2},
 {seat:'Council House',ward:'Concord Ward',width:22,depth:20,height:7.4,tower:14.4,platform:1.44,wardRadius:4,crystal:7.2,guardians:2,spires:2},
 {seat:'Crown Hall',ward:'Sovereign Ward',width:29,depth:25,height:9.2,tower:19,platform:1.92,wardRadius:4.9,crystal:9.6,guardians:4,spires:4},
] as const;
export function civicTier(tier:number) { return CIVIC_TIERS[Math.max(0,Math.min(5,Math.floor(tier)))]; }
/** Conservative envelope includes the entire stair and ground apron. */
export function civicFootprint(tier:number,kind:'admin'|'monument') {
 const r=civicTier(tier);
 return kind==='admin'?{w:r.width+1.6,d:r.depth+6.4}:{w:r.wardRadius*2+1,d:r.wardRadius*2+3.6};
}
export function civicDesign(seed:string,tier:number) {
 const r=rngFromString(`${seed}:civic:massing:${tier}:v2`);
 return {width:.94+r()*.06,depth:.94+r()*.06,wingSide:r()<.5?-1:1,
  wingDepth:.58+r()*.15,pitch:.49+r()*.08,tower:.98+r()*.04,crystal:.96+r()*.08};
}
export function civicBuilding(kind:'admin'|'monument',seed:string,tier:number):FrontierPlacePlan {
 const t=Math.max(0,Math.min(5,Math.floor(tier))),rule=civicTier(t),design=civicDesign(seed,t);
 const detail=rngFromString(`${seed}:civic:${kind}:masonry:v2`);
 const nodes:AssetNode[]=[],obstacles:PlaceBox[]=[],floors:PlaceBox[]=[];
 let serial=0;
 const node=(id:string,kind:AssetNode['kind'],params:number[],p:AssetVec3,material:number,r:AssetVec3=[0,0,0])=>{
  const n:AssetNode={id:`${id}:${serial++}`,kind,params,p,r,material};nodes.push(n);return n;
 };
 const box=(id:string,size:AssetVec3,p:AssetVec3,mat:number,solid=false,floor=false,r:AssetVec3=[0,0,0])=>{
  const n=node(id,'box',size,p,mat,r);
  if(solid||floor)(floor?floors:obstacles).push({center:[...p],size:[...size]});
  return n;
 };
 const array=(n:AssetNode,count:number,offset:AssetVec3)=>{
  if(count>1)n.modifiers=[...(n.modifiers??[]),{type:'array',mode:'linear',count,offset}];
 };
 const taper=(n:AssetNode,factor:number)=>{n.modifiers=[{type:'taper',axis:'y',factor}];return n;};
 // Diagonal sculptural supports use short conservative solids, avoiding a large
 // invisible box across the opening between the stone arms and the crystal.
 const stoneBeam=(id:string,a:AssetVec3,b:AssetVec3,width:number,depth:number)=>{
  const dx=b[0]-a[0],dy=b[1]-a[1],length=pmath.hypot(dx,dy);
  box(id,[width,length,depth],[(a[0]+b[0])/2,(a[1]+b[1])/2,a[2]],4,false,false,[0,0,-pmath.atan2(dx,dy)]);
  const count=Math.max(1,Math.ceil(Math.abs(dx)/.14));
  for(let i=0;i<count;i++)obstacles.push({center:[a[0]+dx*(i+.5)/count,a[1]+dy*(i+.5)/count,a[2]],size:[width+Math.abs(dx)/count,Math.abs(dy)/count+width*.3,depth]});
 };
 const stairs=(id:string,width:number,height:number,front:number,run=.26)=>{
  const count=Math.round(height/.16),rise=height/count;
  for(let i=0;i<count;i++)box(id,[width,height-i*rise,run+.012],[0,(height-i*rise)/2,front+(i+.5)*run],4,false,true);
  if(height>=.8)for(const side of [-1,1]){
   const x=side*(width/2+.17);
   for(let i=0;i<count;i++)box(`${id}:cheek`,[.34,height-i*rise+.18,run+.012],[x,(height-i*rise+.18)/2,front+(i+.5)*run],4,true);
   box(`${id}:coping`,[.4,.14,pmath.hypot(height,count*run)+.06],[x,height/2+.25,front+count*run/2],0,false,false,[pmath.atan2(height,count*run),0,0]);
  }
  return front+count*run;
 };
 const banner=(id:string,x:number,top:number,z:number,w:number,h:number)=>{
  box(`${id}:cloth`,[w,h*.83,.065],[x,top-h*.415,z],5);
  const tail=box(`${id}:tail`,[w,h*.2,.065],[x,top-h*.9,z],5,false,false,[Math.PI,0,0]);taper(tail,0);
  box(`${id}:rod`,[w+.4,.09,.12],[x,top+.08,z],1);
  box(`${id}:seal`,[w*.32,w*.32,.075],[x,top-h*.47,z+.045],7,false,false,[0,0,Math.PI/4]);
 };
 /** Front-facing gables at small tiers; low roofs sit behind the stone towers
  * at larger tiers. Tile rows expand into shared instanced draws. */
 const roof=(id:string,w:number,d:number,y:number,x=0,z=0,pitch=design.pitch)=>{
  const half=w/2,rise=half*pmath.sin(pitch)/pmath.cos(pitch);
  for(const side of [-1,1]){
   taper(box(`${id}:gable`,[Math.max(.3,w-.75),rise,.2],[x,y+rise/2,z+side*(d/2-.38)],0),0);
   box(`${id}:tie`,[w-.4,.16,.18],[x,y+.02,z+side*(d/2-.2)],1);
   box(`${id}:deck`,[half/pmath.cos(pitch),.13,d],[x+side*half/2,y+rise/2,z],1,false,false,[0,0,-side*pitch]);
   for(let row=0;row<5;row++){
    const xx=(row+.5)*half/5;
    const tile=box(`${id}:tile`,[half/5/pmath.cos(pitch)+.07,.08,d/9-.025],[x+side*xx,y+rise-xx*rise/half+.09,z-d/2+d/18],detail()<.5?2:3,false,false,[0,0,-side*pitch]);
    array(tile,9,[0,0,d/9]);
   }
   box(`${id}:eave`,[.14,.18,d+.1],[x+side*half,y,z],1);
   const count=Math.max(4,Math.ceil(rise/.18));
   for(let i=0;i<count;i++)floors.push({center:[x+side*(i+.5)*half/count,y+rise-(i+.5)*rise/count+.05,z],size:[half/count,.16,d]});
  }
  box(`${id}:ridge`,[.17,.18,d+.12],[x,y+rise+.13,z],3);
 };
 const window=(id:string,x:number,y:number,z:number,w=.65,h=1.05)=>{
  box(`${id}:recess`,[w+.18,h+.18,.09],[x,y,z],1);
  box(`${id}:shutter`,[w,h,.11],[x,y,z+.045],5);
  box(`${id}:sill`,[w+.3,.12,.28],[x,y-h/2-.09,z+.05],4);
 };
 const crystal=(id:string,x:number,y:number,z:number,h:number,r:number)=>{
  const rng=rngFromString(`${seed}:civic:${id}:facets:v2`),phase=rng()*pmath.TWO_PI;
  const n=node(id,'lathe',[5+t%3],[x,y,z],8,[0,phase,0]);
  n.profile=[[0,0],[r*.42,h*.2],[r*.77,h*.4],[r,h*(.57+rng()*.04)],[r*.66,h*.79],[0,h]];
  n.faceting=.65+rng()*.3;
 };
 const guardian=(x:number,z:number,y:number,scale:number)=>{
  box('guardian-base',[.65*scale,.2,.65*scale],[x,y+.1,z],4,true);
  node('guardian-robe','cylinder',[.18*scale,.3*scale,.9*scale],[x,y+.2+.45*scale,z],0);
  box('guardian-mantle',[.58*scale,.18*scale,.28*scale],[x,y+.2+.94*scale,z],4);
  node('guardian-head','sphere',[.18*scale],[x,y+.2+1.2*scale,z],0);
  for(const side of [-1,1])box('guardian-arms',[.13*scale,.5*scale,.14*scale],[x+side*.22*scale,y+.2+.76*scale,z+.08],4,false,false,[-.24,0,-side*.1]);
  box('guardian-tablet',[.3*scale,.4*scale,.09],[x,y+.2+.65*scale,z+.2],7);
  obstacles.push({center:[x,y+.2+.65*scale,z],size:[.66*scale,1.4*scale,.66*scale]});
 };
 let entrance:AssetVec3,entryApproach:AssetVec3;
 let civicInterior:CivicInterior|undefined;
 if(kind==='admin'&&t>=4){
  const hall=buildCivicHall({node,box,array,roof,banner,stairs,nodes,obstacles,floors},seed,t,rule,design);
  entrance=hall.entrance;entryApproach=hall.entryApproach;civicInterior=hall.interior;
 }else if(kind==='admin'){
  const w=rule.width*design.width,d=rule.depth*design.depth,h=rule.height;
  const base=rule.platform,zShift=t>=3?-.9:0,front=zShift+d/2,back=zShift-d/2;
  const mainW=t===1?w*.68:t===3?w*.84:w*.88,door=t<3?1.35:1.65+t*.1;
  const platformFront=front+.48;
  box('civic-platform',[w+.7,base,d+.96],[0,base/2,zShift],4,false,true);
  const toe=stairs('public-stair',t<3?2.2+t*.35:3.4+(t-3)*.75,base,platformFront);
  box('back-wall',[mainW,h,.25],[0,base+h/2,back],0,true);
  for(const side of [-1,1]){
   box('side-wall',[.25,h,d],[side*mainW/2,base+h/2,zShift],0,true);
   box('front-pier',[(mainW-door)/2,h,.26],[side*(mainW+door)/4,base+h/2,front],0,true);
   box('door-jamb',[.2,2.5,.42],[side*(door/2+.06),base+1.25,front+.08],4);
   const quoins=box('corner-stones',[.36,.38,.38],[side*(mainW/2-.05),base+.2,front+.03],4);
   array(quoins,Math.min(28,Math.floor(h/.5)),[0,.5,0]);
  }
  box('door-lintel',[door+.35,.25,.45],[0,base+2.6,front+.08],4,true);
  box('above-door',[door,h-2.72,.26],[0,base+2.72+(h-2.72)/2,front],0,true);
  const levels=t<2?1:2;
  for(let level=0;level<levels;level++)for(const side of [-1,1])window('hall-window',side*(door/2+(mainW-door)*.27),base+1.45+level*2.75,front+.17,t<3?.6:.55,t<3?1:1.3);
  for(const side of [-1,1]){
   array(box('side-shutters',[.08,1.05,.7],[side*(mainW/2+.16),base+1.5,back+d*.3],5),levels,[0,2.75,0]);
   array(box('side-sills',[.3,.12,.92],[side*(mainW/2+.17),base+.9,back+d*.3],4),levels,[0,2.75,0]);
  }
  for(let level=1;level<levels;level++){
   box('upper-floor',[mainW-.25,.16,d-.25],[0,base+level*2.75,zShift],1,false,true);
   for(const side of [-1,1])box('floor-course',[.25,.2,d+.3],[side*mainW/2,base+level*2.75,zShift],t===2?1:4);
   box('facade-course',[mainW+.3,.2,.28],[0,base+level*2.75,front+.07],t===2?1:4);
  }
  roof('hall-roof',mainW+.85,d+.8,base+h,0,zShift,t<3?design.pitch:.3);
  if(t<=2){
   // Offset second gable and timber-framed upper floor distinguish the early tiers.
   if(t===1){
    const ww=w*.28,wd=d*design.wingDepth,wx=design.wingSide*(mainW/2+ww/2-.25),wz=front-wd/2+.2;
    box('side-office',[ww,2.65,wd],[wx,base+1.325,wz],0,true);
    roof('office-roof',ww+.5,wd+.5,base+2.65,wx,wz);
    window('office-window',wx,base+1.25,front+.38,.45,.8);
   }
   const porchW=t===0?mainW*.63:door+.75,porchD=.85;
   for(const side of [-1,1])box('porch-post',[.16,2.45,.16],[side*porchW/2,base+1.225,front+porchD],1,true);
   box('porch-deck',[porchW+.3,.13,porchD/pmath.cos(.22)+.3],[0,base+2.6,front+porchD/2],1,false,false,[.22,0,0]);
   array(box('porch-tiles',[(porchW+.3)/8-.018,.1,porchD+.25],[-(porchW+.3)/2+(porchW+.3)/16,base+2.69,front+porchD/2],2,false,false,[.22,0,0]),8,[(porchW+.3)/8,0,0]);
   if(t===2)for(const side of [-1,1]){
    box('timber-upright',[.17,2.75,.18],[side*(mainW/2-.16),base+h-1.375,front+.18],1);
    box('timber-brace',[.14,1.05,.14],[side*(mainW*.31),base+3.23,front+.2],1,false,false,[0,0,side*.65]);
   }
   const chimneyX=-design.wingSide*mainW*.28;
   box('chimney',[.52,1.55,.52],[chimneyX,base+h+.62,back+d*.23],4);
   box('chimney-cap',[.68,.16,.65],[chimneyX,base+h+1.46,back+d*.23],1);
   if(t===0){
    const px=-mainW/2-.42;
    box('office-signpost',[.12,3.8,.12],[px,1.9,front-.2],1,true);
    banner('office-flag',px+.42,3.6,front-.2,.56,1.15);
   }else banner('civic-banner',t===1?-design.wingSide*mainW*.27:0,base+h-.12,front+.3,t===1?.62:.9,t===1?1.25:1.85);
  }else{
   // Full-height entrance tower with a doorway, in front of the lower roof.
   const tw=2.55,th=rule.tower*design.tower,tz=front-.68;
   for(const side of [-1,1])box('gate-tower-pier',[(tw-door)/2,th,2.2],[side*(tw+door)/4,base+th/2,tz],4,true);
   box('gate-tower-upper',[door,th-2.72,2.2],[0,base+2.72+(th-2.72)/2,tz],0,true);
   box('gate-tower-cap',[tw+.38,.23,2.58],[0,base+th+.115,tz],1,false,true);
   banner('tower-banner',0,base+th-.8,tz+1.15,tw*.55,th*.53);
  }
  for(const side of [-1,1])array(box('foundation-ashlar',[.42+detail()*.13,.22,.11],[side*(mainW/2-.32),base+.18,front+.2],4),3,[0,.46,0]);
  box('public-desk',[Math.min(mainW-1.2,2.6),.12,.65],[0,base+.75,zShift-d*.16],1,true);
  for(const side of [-1,1])box('desk-leg',[.12,.7,.5],[side*.8,base+.35,zShift-d*.16],1,true);
  box('ledger',[.32,.06,.25],[.2,base+.85,zShift-d*.16],6);
  box('archive',[.7,1.5,.38],[-mainW/2+.55,base+.75,back+.55],1,true);
  array(box('archive-ledgers',[.54,.18,.3],[-mainW/2+.55,base+.3,back+.76],6),3,[0,.4,0]);
  entrance=[0,0,toe+.06];entryApproach=[0,0,rule.depth/2+3];
 }else{
  const r=rule.wardRadius,base=t<2?.16*(t+2):.16*(t+3),h=rule.crystal*design.crystal,cr=h*.145;
  const rings=t<2?2:3;
  for(let i=0;i<rings;i++)box('ward-terrace',[r*2-i*.3,base*(i+1)/rings,r*1.6-i*.3],[0,base*(i+1)/rings/2,0],4,false,true);
  const toe=stairs('ward-stair',r*.95,base,r*.8-.3);
  taper(box('ward-socket',[Math.max(.65,cr*.95),.36,Math.max(.65,cr*.95)],[0,base+.18,0],4),.65);
  obstacles.push({center:[0,base+.18,0],size:[Math.max(.65,cr*.95),.36,Math.max(.65,cr*.95)]});
  crystal('ward-heart',0,base+.32,0,h,cr);
  obstacles.push({center:[0,base+.32+h/2,0],size:[cr*2,h,cr*2]});
  for(const side of [-1,1])for(let row=0;row<(t===5?2:1);row++){
   const zz=t===5?(row-.5)*cr*.95:0,reach=cr+.24+t*.035,foot=Math.min(reach*.65,r*.32),thickness=.23+t*.065;
   const top=base+.32+h*(t<2?.43:.84),knee=base+h*.22,elbow=base+h*.4;
   stoneBeam('ward-root',[side*foot,base,zz],[side*foot,knee,zz],thickness*1.4,thickness*1.8);
   stoneBeam('ward-elbow',[side*foot,knee,zz],[side*reach,elbow,zz],thickness,thickness*1.4);
   stoneBeam('ward-upright',[side*reach,elbow,zz],[side*reach,top,zz],thickness,thickness*1.4);
   taper(box('ward-chisel-tip',[thickness,.3+t*.08,thickness*1.4],[side*reach,top+.13,zz],4),.55);
  }
  // Guardian statues remain subordinate to the crystal and its stone cradle.
  for(let i=0;i<rule.guardians;i++){
   const side=i%2?1:-1,x=rule.guardians===1?r*.68:side*r*.73,z=i>1?-r*.38:r*.3;
   guardian(x,z,base,.52+t*.07);
  }
  entrance=[0,0,toe+.06];entryApproach=[0,0,r+1.7];
 }
 nodes.push({id:'public-entrance',name:'ground:entrance',kind:'socket',p:entrance,r:[0,0,0]});
 const plan:FrontierPlacePlan={version:1,kind,asset:{version:1,id:`civic-${kind}-${t}`,name:kind==='admin'?rule.seat:rule.ward,kind:'buildingModule',tags:['civic',civicInterior?'civic-halls-v1':'civic-shape-v2',`tier-${t}`],buildingModule:{footprintUnits:[1,1]},materials:[
  {color:0xeee2c9,roughness:.94,metalness:0},{color:0x63452e,roughness:.9,metalness:0},
  {color:0xb96240,roughness:.88,metalness:0},{color:0xc5744c,roughness:.9,metalness:0},
  {color:0xbdb19b,roughness:.95,metalness:0},{color:0x25598a,roughness:.85,metalness:0},
  {color:0xe3c987,roughness:.9,metalness:0},{color:0xbb9750,roughness:.48,metalness:.42},
  {color:0x72cfee,roughness:.23,metalness:.2,emissive:0x167dbc,emissiveIntensity:.4},
  {color:0xbeeafa,roughness:.2,metalness:.1,emissive:0x54b4ef,emissiveIntensity:.45},
  {color:0x364237,roughness:.7,metalness:.35},
  {color:0xffbf65,roughness:.6,metalness:0,emissive:0xff9b36,emissiveIntensity:1.2},
 ],nodes},obstacles,floors,entrance,entryApproach,...(civicInterior?{civicInterior}:{}),slots:kind==='monument'?[{id:'focus',activity:'inspect',position:[0,0,entryApproach[2]-.2],approach:entryApproach,heading:Math.PI,capacity:1}]:[],canopies:[],design:{homeForm:'wing',workshopForm:'forge',storehouseForm:'loft',roofPitch:design.pitch,homeWidth:rule.width,porchWidth:3,marketBays:2,marketHeight:3,crystalHeight:rule.crystal,spires:rule.spires,mirrored:design.wingSide<0}};
 // Keep each asset within the existing authoring limits; a large hall is an
 // assembly, not an exception that raises limits for every procedural asset.
 if(nodes.length>220){
  plan.assetParts=[];
  for(let i=220;i<nodes.length;i+=220)plan.assetParts.push({...plan.asset,id:`${plan.asset.id}-part-${i/220}`,nodes:nodes.slice(i,i+220)});
  plan.asset.nodes=nodes.slice(0,220);
 }
 return plan;
}
